import { useState, useEffect } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/esm/Button";
import { grantAnalyticsConsent, denyAnalyticsConsent } from "../services/firebase";
import { Globals } from "../globals";
import { migrateStorageKey } from "../services/storageMigration";

const CONSENT_KEY = "serva_cookie_consent";
const LEGACY_CONSENT_KEY = "godilog_cookie_consent";

export type ConsentDecision = "accepted" | "declined";

export function getStoredConsent(): ConsentDecision | null {
    migrateStorageKey(LEGACY_CONSENT_KEY, CONSENT_KEY);
    try {
        const stored = localStorage.getItem(CONSENT_KEY);
        if (stored === "accepted" || stored === "declined") return stored;
    } catch {
        // localStorage may be unavailable (e.g. private browsing restrictions)
    }
    return null;
}

export default function CookieConsentBanner() {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const existing = getStoredConsent();
        if (existing === null) {
            setVisible(true);
        } else if (existing === "accepted") {
            grantAnalyticsConsent();
        }
    }, []);

    function handleAccept() {
        try {
            localStorage.setItem(CONSENT_KEY, "accepted");
        } catch {
            // ignore
        }
        grantAnalyticsConsent();
        setVisible(false);
    }

    function handleDecline() {
        try {
            localStorage.setItem(CONSENT_KEY, "declined");
        } catch {
            // ignore
        }
        denyAnalyticsConsent();
        setVisible(false);
    }

    if (!visible) return null;

    return (
        <div
            className="position-fixed bottom-0 start-0 end-0 bg-body-tertiary border-top p-3 d-flex flex-column flex-sm-row align-items-center justify-content-between gap-2"
            style={{ zIndex: 1050 }}
            role="region"
            aria-label={t('cookieConsent.bannerLabel') as string}
        >
            <p className="mb-0 small text-muted">
                {t('cookieConsent.message')}{' '}
                <Link to={Globals.PATH_DATENSCHUTZ} className="text-muted">
                    {t('footer.privacy')}
                </Link>.
            </p>
            <div className="d-flex gap-2 flex-shrink-0">
                <Button variant="outline-secondary" size="sm" onClick={handleDecline}>
                    {t('cookieConsent.decline')}
                </Button>
                <Button variant="primary" size="sm" onClick={handleAccept}>
                    {t('cookieConsent.accept')}
                </Button>
            </div>
        </div>
    );
}
